import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import './ResetPassword.css'; // reuse same styles

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [email, setEmail] = useState('');
  const [invitedBy, setInvitedBy] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(5);

  // Validate token on mount and pre-fill email
  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link.');
      setValidating(false);
      return;
    }
    api.get(`/api/v1/auth/team/invites/accept/?token=${token}`)
      .then((res) => {
        setEmail(res.data.email);
        setInvitedBy(res.data.invited_by || '');
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'This invitation link is invalid or has expired.');
      })
      .finally(() => setValidating(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/v1/auth/team/invites/accept/', { token, name, password });
      setSuccess(true);
      // Countdown then redirect
      let c = 5;
      const iv = setInterval(() => {
        c -= 1;
        setCountdown(c);
        if (c <= 0) { clearInterval(iv); navigate('/login'); }
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-page">
      <div className="rp-container">
        <div className="rp-header">
          <div className="rp-logo">
            <div className="logo-square">LS</div>
            <div className="logo-text">
              <div className="logo-title">LegalSoft</div>
              <div className="logo-subtitle">AI Matchmaker</div>
            </div>
          </div>
        </div>

        <div className="rp-content">
          {validating ? (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>Validating invitation…</p>
          ) : success ? (
            <div className="rp-success">
              <div className="rp-success-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="#10b981" />
                </svg>
              </div>
              <h1 className="rp-title">Welcome to the team!</h1>
              <p className="rp-subtitle">
                Your account has been created. Sign in with your email address{' '}
                <strong>{email}</strong> and the password you just set.
              </p>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: 4 }}>
                Redirecting to sign in in {countdown}s…
              </p>
              <Link to="/login" className="rp-back-link">Sign in now →</Link>
            </div>
          ) : (
            <>
              <div className="rp-icon-wrap">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="#8b5cf6" />
                </svg>
              </div>
              <h1 className="rp-title">Set up your account</h1>
              <p className="rp-subtitle">
                {invitedBy
                  ? <><strong>{invitedBy}</strong> has invited you to join LegalSoft AI Matchmaker.</>
                  : 'Complete your account to join LegalSoft AI Matchmaker.'}
              </p>

              {error && (
                <div className="rp-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor" />
                  </svg>
                  <span>{error}</span>
                </div>
              )}

              {!error && (
                <form onSubmit={handleSubmit} className="rp-form">
                  <div className="rp-form-group">
                    <label className="rp-label">Email address</label>
                    <input
                      type="email"
                      className="rp-input"
                      value={email}
                      readOnly
                      style={{ background: '#f9fafb', color: '#6b7280' }}
                    />
                  </div>

                  <div className="rp-form-group">
                    <label className="rp-label">Full name</label>
                    <input
                      type="text"
                      className="rp-input"
                      placeholder="Jane Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoFocus
                    />
                  </div>

                  <div className="rp-form-group">
                    <label className="rp-label">Password</label>
                    <div className="rp-input-wrap">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="rp-input"
                        placeholder="Minimum 8 characters"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="rp-show-password"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="rp-form-group">
                    <label className="rp-label">Confirm password</label>
                    <div className="rp-input-wrap">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="rp-input"
                        placeholder="Repeat your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        className="rp-show-password"
                        onClick={() => setShowPassword((v) => !v)}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="currentColor" />
                          </svg>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="currentColor" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="rp-button" disabled={loading}>
                    {loading ? 'Creating account…' : 'Create Account & Join'}
                  </button>
                </form>
              )}

              <Link to="/login" className="rp-back-link">← Back to sign in</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AcceptInvite;
